import { useEffect } from 'react'

import { api } from '../api/client'
import type { Nis2Report } from '../api/types'
import { formatDateTime } from '../components/common'
import { useAsync } from '../hooks/useAsync'
import { maiuscola, t } from '../i18n/lingua'
import { CRITICALITY_LABEL } from './Services'

/**
 * Il report di conformità, pronto per la stampa (e quindi per il PDF).
 *
 * ## Perché una vista e non un generatore di PDF
 *
 * Il PDF lo fa il browser (`window.print` → «Salva come PDF»), che lo fa bene
 * e gratis: un generatore lato server sarebbe una dipendenza in più per
 * riprodurre peggio ciò che il motore di stampa già sa fare. La vista è HTML
 * con un CSS di stampa: si controlla a video esattamente ciò che uscirà.
 *
 * ## La regola del documento
 *
 * Le due metà restano **etichettate**: ciò che la piattaforma ha misurato dai
 * dati raccolti, e ciò che l'organizzazione ha dichiarato di coprire altrove.
 * Sommarle in un numero unico è il difetto per cui i report di conformità
 * falliscono gli audit — questo documento non lo fa, e lo scrive.
 */

const ETICHETTA_FASCIA: Record<string, () => string> = {
  critico: () => t('critico'),
  parziale: () => t('parziale'),
  da_verificare: () => t('da verificare'),
  conforme: () => t('conforme'),
}

const ETICHETTA_STATO: Record<string, () => string> = {
  coperto: () => t('coperto'),
  parziale: () => t('parzialmente coperto'),
  assente: () => t('non coperto'),
  non_applicabile: () => t('non applicabile'),
}

export default function ConformitaReport({
  dati,
  titolo,
  sottotitolo,
  onChiudi,
}: {
  dati: Nis2Report
  titolo: string
  sottotitolo: string
  onChiudi: () => void
}) {
  const me = useAsync(() => api.me(), [])

  // Esc chiude: la vista copre l'app e senza una via d'uscita da tastiera
  // sembrerebbe una pagina rotta.
  useEffect(() => {
    const esc = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onChiudi()
    }
    window.addEventListener('keydown', esc)
    return () => window.removeEventListener('keydown', esc)
  }, [onChiudi])

  const totale = Object.values(dati.fasce).reduce((a, b) => a + b, 0)
  const nonConformi = dati.assets.filter(
    (a) => a.fascia === 'critico' || a.fascia === 'parziale',
  )
  const dichiarate = dati.non_misurato.filter((v) => v.declaration)
  const scoperte = dati.non_misurato.filter((v) => !v.declaration)

  return (
    <div className="report-print">
      <div className="report-print__barra no-print">
        <button type="button" onClick={() => window.print()}>
          {t('Stampa / salva PDF')}
        </button>
        <button type="button" className="secondary" onClick={onChiudi}>
          {t('Chiudi')}
        </button>
      </div>

      <article className="report">
        <header className="report__testata">
          <h1>{titolo}</h1>
          <p className="report__sotto">{sottotitolo}</p>
          <table className="report__meta">
            <tbody>
              <tr>
                <th>{t('Organizzazione')}</th>
                <td>{me.data?.tenant_name || me.data?.tenant_slug || '—'}</td>
              </tr>
              <tr>
                <th>{t('Generato il')}</th>
                <td>{formatDateTime(dati.generato_al)}</td>
              </tr>
              <tr>
                <th>{t('Asset considerati')}</th>
                <td>{totale}</td>
              </tr>
            </tbody>
          </table>
        </header>

        <section>
          <h2>{t('Sintesi')}</h2>
          <table className="report__tabella">
            <thead>
              <tr>
                {(['critico', 'parziale', 'da_verificare', 'conforme'] as const).map((f) => (
                  <th key={f}>{maiuscola(ETICHETTA_FASCIA[f]())}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              <tr>
                {(['critico', 'parziale', 'da_verificare', 'conforme'] as const).map((f) => (
                  <td key={f} className="report__numero">{dati.fasce[f] ?? 0}</td>
                ))}
              </tr>
            </tbody>
          </table>
          <p className="report__nota">
            {t('Le fasce nascono dai soli controlli misurati; «da verificare» segnala asset per cui mancano i dati, non asset a posto.')}
          </p>
        </section>

        <section>
          <h2>{t('Requisiti misurati dalla piattaforma')}</h2>
          <table className="report__tabella">
            <thead>
              <tr>
                <th>{t('Articolo')}</th>
                <th>{t('Controllo')}</th>
                <th>{t('Superato')}</th>
                <th>{t('Fallito')}</th>
                <th>{t('Non valutabile')}</th>
                <th>{t('Non applicabile')}</th>
              </tr>
            </thead>
            <tbody>
              {dati.requisiti.map((r) => (
                <tr key={r.id}>
                  <td className="mono">{r.articolo}</td>
                  <td>{t(r.titolo)}</td>
                  <td className="report__numero">{r.ok}</td>
                  <td className="report__numero">{r.ko}</td>
                  <td className="report__numero">{r.non_valutabili}</td>
                  <td className="report__numero">{r.non_applicabili}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </section>

        {dati.evidenze && (
          <section>
            <h2>{t('Evidenze dalla piattaforma')}</h2>
            <table className="report__tabella">
              <tbody>
                <tr><td>{t('Certificati TLS letti dall’agente')}</td><td className="report__numero">{dati.evidenze.certificati.totali}</td></tr>
                <tr><td>{t('scaduti')}</td><td className="report__numero">{dati.evidenze.certificati.scaduti}</td></tr>
                <tr><td>{t('in scadenza entro 30 giorni')}</td><td className="report__numero">{dati.evidenze.certificati.in_scadenza_30}</td></tr>
                <tr><td>{t('autofirmati')}</td><td className="report__numero">{dati.evidenze.certificati.autofirmati}</td></tr>
                <tr><td>{t('Servizi chiamati in chiaro da altre macchine')}</td><td className="report__numero">{dati.evidenze.in_chiaro.servizi}</td></tr>
                <tr><td>{t('Asset con distinta dei componenti (SBOM)')}</td><td className="report__numero">{dati.evidenze.sbom.asset}</td></tr>
                <tr><td>{t('Librerie vulnerabili')}</td><td className="report__numero">{dati.evidenze.sbom.librerie_vulnerabili}</td></tr>
                <tr><td>{t('sfruttate attivamente (CISA KEV)')}</td><td className="report__numero">{dati.evidenze.sbom.sfruttate}</td></tr>
                <tr>
                  <td>{t('Asset che sostengono funzioni critiche o importanti, monitorati')}</td>
                  <td className="report__numero">
                    {dati.evidenze.monitoraggio.in_uso
                      ? `${dati.evidenze.monitoraggio.asset_importanti_monitorati} / ${dati.evidenze.monitoraggio.asset_importanti}`
                      : '—'}
                  </td>
                </tr>
              </tbody>
            </table>
          </section>
        )}

        {dati.dora && (
          <section>
            <h2>{t('Funzioni critiche o importanti (art. 8)')}</h2>
            {dati.dora.funzioni_critiche.apps.length === 0 ? <p>{t('Nessuna Business App definita.')}</p> : (
              <table className="report__tabella">
                <thead>
                  <tr><th>{t('Business App')}</th><th>{t('Criticità')}</th><th>{t('Funzioni')}</th><th>{t('Asset')}</th><th>{t('Non conformi')}</th></tr>
                </thead>
                <tbody>
                  {dati.dora.funzioni_critiche.apps.map((a) => (
                    <tr key={a.id}>
                      <td>{a.name}</td>
                      <td>{a.criticality ? CRITICALITY_LABEL[a.criticality] ?? a.criticality : '—'}</td>
                      <td>{a.functions.map((f) => f.name).join(', ') || '—'}</td>
                      <td className="report__numero">{a.cis}</td>
                      <td className="report__numero">{a.non_compliant}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
            <h2>{t('Fornitori ICT terzi e concentrazione (artt. 28–29)')}</h2>
            {dati.dora.fornitori_ict.vendors.length === 0 ? <p>{t('Nessun contratto registrato e nessun provider cloud nell’inventario.')}</p> : (
              <table className="report__tabella">
                <thead>
                  <tr><th>{t('Fornitore')}</th><th>{t('Contratti')}</th><th>{t('Asset critici')}</th><th>{t('Concentrazione')}</th><th>{t('Da sistemare')}</th></tr>
                </thead>
                <tbody>
                  {dati.dora.fornitori_ict.vendors.map((v) => (
                    <tr key={v.vendor}>
                      <td>{v.vendor}</td>
                      <td>{v.contracts.map((c) => c.reference).join(', ') || '—'}</td>
                      <td className="report__numero">{v.critical_cis}</td>
                      <td className="report__numero">{v.concentration == null ? '—' : `${v.concentration}%`}</td>
                      <td>{v.issues.join('; ') || '—'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </section>
        )}

        <section>
          <h2>{t('Autovalutazione dell’organizzazione (non misurato dalla piattaforma)')}</h2>
          <p className="report__nota">
            {t('Quanto segue è dichiarato dall’organizzazione, non misurato dai dati raccolti: le due cose restano separate di proposito.')}
          </p>
          {dichiarate.length === 0 ? (
            <p>{t('Nessuna dichiarazione compilata.')}</p>
          ) : (
            <table className="report__tabella">
              <thead>
                <tr>
                  <th>{t('Articolo')}</th>
                  <th>{t('Requisito')}</th>
                  <th>{t('Stato dichiarato')}</th>
                  <th>{t('Riferimento')}</th>
                  <th>{t('Responsabile')}</th>
                  <th>{t('Aggiornata')}</th>
                </tr>
              </thead>
              <tbody>
                {dichiarate.map((v) => (
                  <tr key={v.id}>
                    <td className="mono">{v.articolo}</td>
                    <td>
                      {t(v.titolo)}
                      {v.declaration!.nota && (
                        <div className="report__nota-riga">{v.declaration!.nota}</div>
                      )}
                    </td>
                    <td>{ETICHETTA_STATO[v.declaration!.stato]?.() ?? v.declaration!.stato}</td>
                    <td>{v.declaration!.riferimento || '—'}</td>
                    <td>{v.declaration!.responsabile || '—'}</td>
                    <td>
                      {v.declaration!.updated_at
                        ? formatDateTime(v.declaration!.updated_at)
                        : '—'}
                      {v.declaration!.updated_by && (
                        <div className="report__nota-riga">{v.declaration!.updated_by}</div>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
          {scoperte.length > 0 && (
            <>
              <h3>{t('Requisiti senza dichiarazione')}</h3>
              <ul>
                {scoperte.map((v) => (
                  <li key={v.id}>
                    <span className="mono">{v.articolo}</span> — {t(v.titolo)}
                    {(v.evidenze?.length ?? 0) > 0 && <div className="report__nota-riga">{v.evidenze!.join(' · ')}</div>}
                  </li>
                ))}
              </ul>
            </>
          )}
        </section>

        {dati.azioni.length > 0 && (
          <section>
            <h2>{t('Piano di lavoro')}</h2>
            <table className="report__tabella">
              <thead>
                <tr>
                  <th>{t('Controllo')}</th>
                  <th>{t('Cosa fare')}</th>
                  <th>{t('Asset coinvolti')}</th>
                  <th>{t('Esempi')}</th>
                </tr>
              </thead>
              <tbody>
                {dati.azioni.map((a, i) => (
                  <tr key={i}>
                    <td>
                      {t(a.titolo)} <span className="mono">({a.articolo})</span>
                    </td>
                    <td>
                      {a.tipo === 'ko'
                        ? t('Rimediare: {motivo}', { motivo: a.motivo })
                        : t('Misurare: {motivo}', { motivo: a.motivo })}
                    </td>
                    <td className="report__numero">{a.asset_count}</td>
                    <td>{a.esempi.join(', ')}{a.asset_count > a.esempi.length ? '…' : ''}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </section>
        )}

        {nonConformi.length > 0 && (
          <section>
            <h2>{t('Allegato: asset non conformi ({n})', { n: nonConformi.length })}</h2>
            <table className="report__tabella">
              <thead>
                <tr>
                  <th>{t('Asset')}</th>
                  <th>{t('Fascia')}</th>
                  <th>{t('Controlli falliti')}</th>
                </tr>
              </thead>
              <tbody>
                {nonConformi.map((a) => (
                  <tr key={a.ci_id}>
                    <td>{a.name ?? a.ci_id.slice(0, 8)}</td>
                    <td>{maiuscola(ETICHETTA_FASCIA[a.fascia]?.() ?? a.fascia)}</td>
                    <td>
                      {Object.entries(a.controlli)
                        .filter(([, c]) => c.esito === 'ko')
                        .map(([id, c]) => `${id}: ${c.motivo}`)
                        .join('; ') || '—'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </section>
        )}

        <footer className="report__piede">
          <p>
            {t('Nota metodologica: la parte misurata nasce dai dati raccolti dalla piattaforma (inventario, rischi di sicurezza, backup delle configurazioni) al momento della generazione. L’autovalutazione è dichiarata dall’organizzazione e non è verificata dalla piattaforma. Questo documento non costituisce una certificazione.')}
          </p>
        </footer>
      </article>
    </div>
  )
}
